import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import PersonEmploymentDialog from './PersonEmploymentDialog';
import { getPeopleEmployments, deletePersonEmployment } from '../../services/userService';
import { EMPLOYMENT_GRID_COLUMNS } from '../../constants/personEmploymentFormConstants';
import FormSpinner from 'components/FormSpinner';

const PersonEmploymentTab = ({ personId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [employments, setEmployments] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingEmployment, setEditingEmployment] = useState(null);
  const peoplePerms = usePermissions(PERMISSION_RESOURCES.PERSON);
  const employeesPerms = usePermissions(PERMISSION_RESOURCES.EMPLOYEE);
  const canAddEmployment = Boolean(peoplePerms.Edit) && Boolean(employeesPerms.Create);
  const canEditEmployment = Boolean(peoplePerms.Edit) && Boolean(employeesPerms.Edit);
  const canDeleteEmployment = Boolean(peoplePerms.Edit) && Boolean(employeesPerms.Delete);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadEmployments = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPeopleEmployments(personId),
      onSuccess: (data) => setEmployments(data),
    });
  }, [dispatch, personId]);

  const handleAdd = useCallback(() => {
    setEditingEmployment(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingEmployment(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingEmployment(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadEmployments();
  }, [handleFormClose, loadEmployments]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${
          row.PlaceName || 'employment'
        }" record?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () =>
              deletePersonEmployment({ ptsPersonID: row.ptsPersonID, ptsPlaceID: row.ptsPlaceID }),
            onSuccess: () => {
              dispatch(notify('Employment deleted successfully', 'success'));
              loadEmployments();
            },
          });
        },
        'Delete Employment'
      );
    },
    [dispatch, requestConfirm, loadEmployments]
  );

  useEffect(() => {
    loadEmployments();
  }, [loadEmployments]);

  return (
    <Box className="person-names-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Employment"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canAddEmployment}
        />
      </Box>

      <CommonDataGrid
        rows={employments}
        columns={EMPLOYMENT_GRID_COLUMNS}
        getRowId={(row) => row.rowKey}
        toolbar={getDataGridToolbarProps({ printTitle: 'Employments' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditEmployment },
          delete: { visible: true, onClick: handleDelete, disabled: !canDeleteEmployment },
        }}
      />

      {formOpen && (
        <PersonEmploymentDialog
          open={formOpen}
          personId={personId}
          employment={editingEmployment}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default PersonEmploymentTab;
